import { fabric } from 'fabric';
import pull from 'lodash-es/pull';
import keyboard from '../keyboard';
import variables from '../variables';
import {
  getGradientAngleFromFabricGradientCoords,
  getFabricGradient,
} from '../gradients';
import { Bezier } from 'bezier-js';

const HANDLE_RADIUS = 10;
const MIN_THICKNESS = 2;
const MAX_THICKNESS = 250;
const RAD_360 = Math.PI * 2;
// const PADDING = 10;
const arcsFromCircle = (offsetX, r, obj, index) => {
  const kappa = 0.551976835276946;
  const posX = offsetX;
  const maxX = r + posX;
  const minX = -r + posX;
  const rKappa = r * kappa;
  const minXKappa = -rKappa + posX;
  const maxXKappa = rKappa + posX;

  const arcs = [
    [
      { x: maxX, y: 0 },
      { x: maxX, y: rKappa },
      { x: maxXKappa, y: r },
      { x: posX, y: r },
    ],
    [
      { x: posX, y: r },
      { x: minXKappa, y: r },
      { x: -r + posX, y: rKappa },
      { x: -r + posX, y: 0 },
    ],
    [
      { x: minX, y: 0 },
      { x: minX, y: -rKappa },
      { x: minXKappa, y: -r },
      { x: posX, y: -r },
    ],
    [
      { x: posX, y: -r },
      { x: posX + rKappa, y: -r },
      { x: maxX, y: -rKappa },
      { x: maxX, y: 0 },
    ],
  ];

  if (!obj || !obj[index]) {
    return new Bezier(...Object.values(arcs[index]));
  } else {
    obj[index].points = arcs[index];
    obj[index].update();
    return obj[index];
  }
};
const drawCurve = (ctx, curve, offset) => {
  offset = offset || { x: 0, y: 0 };
  var ox = offset.x;
  var oy = offset.y;
  var p = curve.points || curve.left.points;
  ctx.lineTo(p[0].x + ox, p[0].y + oy);
  if (p.length === 3) {
    ctx.quadraticCurveTo(p[1].x + ox, p[1].y + oy, p[2].x + ox, p[2].y + oy);
  }
  if (p.length === 4) {
    ctx.bezierCurveTo(
      p[1].x + ox,
      p[1].y + oy,
      p[2].x + ox,
      p[2].y + oy,
      p[3].x + ox,
      p[3].y + oy,
    );
  }
};

const drawLine = (ctx, p1, p2, offset) => {
  offset = offset || { x: 0, y: 0 };
  var ox = offset.x;
  var oy = offset.y;
  ctx.lineTo(p1.x + ox, p1.y + oy);
  ctx.lineTo(p2.x + ox, p2.y + oy);
};

const getPointOnLine = (x1, x2, y1, y2, len) => {
  var xlen = x2 - x1;
  var ylen = y2 - y1;
  var smallerXLen = xlen * len;
  var smallerYLen = ylen * len;
  return { x: x1 + smallerXLen, y: y1 + smallerYLen };
};

const getTriangleSizeByThickness = arrowSize => arrowSize;
const toFixed = fabric.util.toFixed;

const extrapolatePoints = (p1, p2, ratio) =>
  new fabric.Point(p1.x + (p2.x - p1.x) * ratio, p1.y + (p2.y - p1.y) * ratio);

const getSnapAngleForAngle = angle => {
  const snapAngleStep = 15;
  const minAngle = Math.floor(angle / snapAngleStep) * snapAngleStep;
  const maxAngle = Math.ceil(angle / snapAngleStep) * snapAngleStep;
  if (Math.abs(angle - minAngle) < Math.abs(angle - maxAngle)) {
    return minAngle;
  }
  return maxAngle;
};

const canvasEventProxy = (listener, _this) => o => {
  if (_this.canvas) {
    listener.call(_this, o);
  }
};

const pointProperties = 'x1 y1 x2 y2'.split(' ');

const ownProperties = 'arrowSize arrow1 arrow2 cp'.split(' ');
const cacheProperties = fabric.Object.prototype.cacheProperties.concat(
  ownProperties,
);

const LudusLine = fabric.util.createClass(fabric.Object, {
  type: 'ludusLine',
  arrowSize: 0,
  arrow1: false,
  arrow2: false,
  strokeWidth: 1,
  originX: 'center',
  originY: 'center',
  hasBorders: true,
  lockSkewingX: true,
  lockSkewingY: true,
  lockUniScaling: false,
  length: 0,
  padding: 0,
  perPixelTargetFind: false,
  cacheProperties,
  cp: { x: 0, y: 0 },
  scaleX: 1,
  scaleY: 1,
  p1: { x: -50, y: 0 },
  p2: { x: 50, y: 0 },
  canCache: true,
  initialize(options) {
    // if no strokeWidth, rendering issue can occur

    const optionsFixed = {
      ...options,
      originX: 'center',
      originY: 'center',
      ludus: { ...options.ludus, type: 'line' },
    };

    if (options[1]) {
      // object to migrate
      const p1 = new fabric.Point(options[0], options[1]);
      const p2 = { x: options[2], y: options[3] };
      const midPoint = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

      const width = p1.distanceFrom(p2);
      const angle = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
      optionsFixed.left = midPoint.x;
      optionsFixed.top = midPoint.y;
      optionsFixed.width = width;
      optionsFixed.height = 5;
      optionsFixed.angle = angle;
    }
    if (options.thickness) {
      // migrate data
      optionsFixed.arrowSize = options.thickness ** (8 / 10) * 10 - 6;
      optionsFixed.stroke = options.fill;
      optionsFixed.strokeWidth = options.thickness;

      delete optionsFixed.thickness;
    }

    this.callSuper('initialize', optionsFixed);

    // Set all controls hidden here and keep 'hasControls' to true, so we can hook into
    // Fabric internal controls drawing system.
    this.setControlsVisibility({
      tl: false,
      tr: false,
      br: false,
      bl: false,
      ml: false,
      mt: false,
      mr: false,
      mb: false,
      mtr: false,
    });

    this.lockSkewingX = true;
    this.lockSkewingY = true;
    this.skewX = 0;
    this.skewY = 0;
    this.on('mousedblclick', e => {
      const pointer = this.canvas.getPointer(e);
      if (this.isPointInsideControlPoint(pointer)) {
        this.resetControlPoint(e);
      }
    });

    this.applyStrokeStyle();
    const pt1 = this.getLocalP1();
    const pt2 = this.getLocalP2();
    this.curve = new Bezier(pt1.x, pt1.y, this.cp.x, this.cp.y, pt2.x, pt2.y);
    this.canvasSelectionClearedHandler = canvasEventProxy(
      this.onCanvasSelectionCleared,
      this,
    );
    this.canvasMouseDownHandler = canvasEventProxy(
      this.onCanvasMouseDown,
      this,
    );
    this.canvasDblClickHandler = canvasEventProxy(this.onCanvasDblClick, this);

    this.canvasMouseUpHandler = canvasEventProxy(this.onCanvasMouseUp, this);
    this.canvasMouseMoveHandler = canvasEventProxy(
      this.onCanvasMouseMove,
      this,
    );
    this.shiftKeyActive = false;
    this.initialized = true;
    this._previousState = {};
  },

  getPointsFromCoordsArray(array) {
    const x1 = array[0];
    const y1 = array[1];
    const x2 = array[2];
    const y2 = array[3];
    return {
      p1: new fabric.Point(x1, y1),
      p2: new fabric.Point(x2, y2),
    };
  },

  getMaxThicknessByLength(length) {
    let arrowsVisible = 0;
    if (this.arrow1) arrowsVisible += 1;
    if (this.arrow2) arrowsVisible += 1;
    if (arrowsVisible === 0) {
      return MAX_THICKNESS;
    }
    return Math.min(
      MAX_THICKNESS,
      ((length / arrowsVisible + 6) / 10) ** (10 / 8),
    );
  },

  getMinLengthFromArrowSize() {
    const size = getTriangleSizeByThickness(this.arrowSize);
    let arrowsVisible = 0;
    if (this.arrow1) arrowsVisible += 1;
    if (this.arrow2) arrowsVisible += 1;
    return size * arrowsVisible;
  },

  getLocalP1() {
    return new fabric.Point(-this.width / 2, 0);
  },

  getLocalP2() {
    return new fabric.Point(this.width / 2, 0);
  },

  getGlobalPoint(localPoint) {
    const radians = fabric.util.degreesToRadians(this.angle);
    const rotatedPoint = fabric.util.rotatePoint(
      localPoint,
      new fabric.Point(0, 0),
      radians,
    );
    return rotatedPoint.multiply(this.scaleX).add(this.getCenterPoint());
  },

  /**
   * Checks if object intersects with an area formed by 2 points
   * @param {Object} pointTL top-left point of area
   * @param {Object} pointBR bottom-right point of area
   * @param {Boolean} [absolute] use coordinates without viewportTransform
   * @param {Boolean} [calculate] use coordinates of current position instead of .oCoords
   * @return {Boolean} true if object intersects with an area formed by 2 points
   */
  // intersectsWithRect: function(pointTL, pointBR, absolute, calculate) {
  //   var coords = this.getCoords(absolute, calculate);
  //   var intersection = fabric.Intersection.intersectPolygonRectangle(
  //     coords,
  //     pointTL,
  //     pointBR,
  //   );
  //   return intersection.status === 'Intersection';
  // },

  resetControlPoint: function(e) {
    this.setCp({ x: 0, y: 0 });
    this.setCoords();
    this.canvas.requestRenderAll();
    this.canvas.fire('object:modified', { target: this, e });
  },

  calcCoords: function(absolute) {
    const multiplyMatrices = fabric.util.multiplyTransformMatrices;
    const transformPoint = fabric.util.transformPoint;
    const degreesToRadians = fabric.util.degreesToRadians;
    var startMatrix = this.calcTransformMatrix(true);
    var vpt = this.getViewportTransform();
    const curve = this.curve;
    const bbox = curve.bbox();
    var finalMatrix = absolute
      ? startMatrix
      : multiplyMatrices(vpt, startMatrix);

    const ySize = bbox.y.size;
    const zoom = this.canvas ? this.canvas.getZoom() : 1;
    const paddingArea =
      ySize > 10 ? 5 / zoom : 5 / zoom + Math.max(this.strokeWidth / 2, 5);

    const yMax = ySize > 10 ? bbox.y.max : 1;
    const yMin = ySize > 10 ? bbox.y.min : -1;

    var tl = transformPoint(
      { x: bbox.x.min - this.strokeWidth / 2, y: yMax + paddingArea },
      finalMatrix,
    );
    var tr = transformPoint(
      { x: bbox.x.max + this.strokeWidth / 2, y: yMax + paddingArea },
      finalMatrix,
    );
    var bl = transformPoint(
      { x: bbox.x.min - this.strokeWidth / 2, y: yMin - paddingArea },
      finalMatrix,
    );
    var br = transformPoint(
      { x: bbox.x.max + this.strokeWidth / 2, y: yMin - paddingArea },
      finalMatrix,
    );
    if (!absolute) {
      var padding = this.padding;
      var angle = degreesToRadians(this.angle);
      var cos = fabric.util.cos(angle);
      var sin = fabric.util.sin(angle);
      var cosP = cos * padding;
      var sinP = sin * padding;
      var cosPSinP = cosP + sinP;
      var cosPMinusSinP = cosP - sinP;
      if (padding) {
        tl.x -= cosPMinusSinP;
        tl.y -= cosPSinP;
        tr.x += cosPSinP;
        tr.y -= cosPMinusSinP;
        bl.x -= cosPSinP;
        bl.y += cosPMinusSinP;
        br.x += cosPMinusSinP;
        br.y += cosPSinP;
      }
      var ml = new fabric.Point((tl.x + bl.x) / 2, (tl.y + bl.y) / 2);
      var mt = new fabric.Point((tr.x + tl.x) / 2, (tr.y + tl.y) / 2);
      var mr = new fabric.Point((br.x + tr.x) / 2, (br.y + tr.y) / 2);
      var mb = new fabric.Point((br.x + bl.x) / 2, (br.y + bl.y) / 2);
      var mtr = new fabric.Point(
        mt.x + sin * this.rotatingPointOffset,
        mt.y - cos * this.rotatingPointOffset,
      );
    }

    var coords = {
      // corners
      tl: tl,
      tr: tr,
      br: br,
      bl: bl,
    };
    if (!absolute) {
      // middle
      coords.ml = ml;
      coords.mt = mt;
      coords.mr = mr;
      coords.mb = mb;
      // rotating point
      coords.mtr = mtr;
    }
    return coords;
  },

  getGlobalP1() {
    return new fabric.Point(this.x1, this.y1);
  },

  getGlobalP2() {
    return new fabric.Point(this.x2, this.y2);
  },

  getTensionHandleCoords(absolute) {
    let point = this.cp;
    const midPoint = { x: this.cp.x / 2, y: this.cp.y / 2 };
    if (absolute) {
      const transformMatrix = this.calcTransformMatrix();
      point = fabric.util.transformPoint(midPoint, transformMatrix);
    }

    return point;
  },

  setCp(p, pOnCurve) {
    this.cp = pOnCurve ? getPointOnLine(0, p.x, 0, p.y, 2) : p;
    this.curve.points[1] = this.cp;

    this.curve.update();
    this.bbox = this.curve.bbox();
  },

  setFromHandle(action, x, y) {
    const cursorPos = new fabric.Point(x, y);
    const transformMatrix = this.calcTransformMatrix();
    const intertTransform = fabric.util.invertTransform(transformMatrix);
    const localCursorPos = fabric.util.transformPoint(
      cursorPos,
      intertTransform,
    );
    const distFromCenter = localCursorPos.distanceFrom({ x: 0, y: 0 });

    if (distFromCenter > 15) {
      this.setCp({ x: localCursorPos.x, y: localCursorPos.y }, true);
    } else {
      this.setCp({ x: 0, y: 0 });
    }
  },

  isPointInsideTarget(point, targetPoint, radius) {
    return (
      targetPoint.distanceFrom(point) < (radius * 2) / this.canvas.getZoom()
    );
  },

  isPointInsideHandle(point, handlePoint) {
    return this.isPointInsideTarget(point, handlePoint, HANDLE_RADIUS);
  },

  isPointInsideP1Handle(point) {
    return this.isPointInsideHandle(point, this.getGlobalP1());
  },

  isPointInsideP2Handle(point) {
    return this.isPointInsideHandle(point, this.getGlobalP2());
  },

  isPointInsideControlPoint(point) {
    if (this.cpVisible) {
      return this.isPointInsideHandle(point, this.getTensionHandleCoords(true));
    } else {
      return false;
    }
  },

  setCoords(ignoreZoom, skipAbsolute) {
    this.updateCoords();
    const coords = this.callSuper('setCoords', ignoreZoom, skipAbsolute);
    this.updateGradient();
    return coords;
  },

  getCoords(absolute, calculate) {
    if (!this.oCoords) {
      this.setCoords();
    }
    if (absolute) {
      const tl = {
        x: this.x1,
        y: this.y1,
      };
      const tr = {
        x: this.x2,
        y: this.y1,
      };
      const br = {
        x: this.x1,
        y: this.y2,
      };
      const bl = {
        x: this.x2,
        y: this.y2,
      };
      this.aCoords = {
        tl,
        tr,
        br,
        bl,
      };
    }
    const coords = absolute ? this.aCoords : this.oCoords;
    return this.getCoordsAsPoints(
      calculate ? this.calcCoords(absolute) : coords,
    );
  },
  isOnScreen: function(calculate) {
    if (!this.canvas) {
      return false;
    }
    var pointTL = this.canvas.vptCoords.tl;
    var pointBR = this.canvas.vptCoords.br;
    var points = this.callSuper('getCoords', true, calculate);
    var point;
    for (var i = 0; i < 4; i++) {
      point = points[i];
      if (
        point.x <= pointBR.x &&
        point.x >= pointTL.x &&
        point.y <= pointBR.y &&
        point.y >= pointTL.y
      ) {
        return true;
      }
    }
    // no points on screen, check intersection with absolute coordinates
    if (this.intersectsWithRect(pointTL, pointBR, true, calculate)) {
      const intersection = fabric.Intersection.intersectPolygonRectangle(
        points,
        pointTL,
        pointBR,
      );
      return intersection.status === 'Intersection';
    }
    return this._containsCenterOfCanvas(pointTL, pointBR, calculate);
  },
  getCoordsAsPoints(coords) {
    return [
      new fabric.Point(coords.tl.x, coords.tl.y),
      new fabric.Point(coords.tr.x, coords.tr.y),
      new fabric.Point(coords.br.x, coords.br.y),
      new fabric.Point(coords.bl.x, coords.bl.y),
    ];
  },
  intersectsWithRect: function(pointTL, pointBR, absolute, calculate) {
    var coords = this.callSuper('getCoords', absolute, calculate);
    var intersection = fabric.Intersection.intersectPolygonRectangle(
      coords,
      pointTL,
      pointBR,
    );
    return intersection.status === 'Intersection';
  },

  updateCoords() {
    const height = Math.max(this.strokeWidth, 2);
    this.updateP1Coords(this.getGlobalPoint(this.getLocalP1()));
    this.updateP2Coords(this.getGlobalPoint(this.getLocalP2()));
    this.length = this.getLineMeasuresBetweenPoints(
      this.getGlobalP1(),
      this.getGlobalP2(),
    ).distance;

    if (this.width !== this.length || this.height !== height) {
      // avoid the setter on width, which will update all the points and update the width again...
      // especially visible when snapping guidelines
      this.set({
        height,
      });
    }
  },

  updateP1Coords(coords) {
    this.x1 = toFixed(coords.x, variables.numberPrecision);
    this.y1 = toFixed(coords.y, variables.numberPrecision);
  },

  updateP2Coords(coords) {
    this.x2 = toFixed(coords.x, variables.numberPrecision);
    this.y2 = toFixed(coords.y, variables.numberPrecision);
  },

  updatePointsCoords(w) {
    if (this.curve) {
      this.curve.points[0].x = -w / 2;
      this.curve.points[2].x = w / 2;
      this.curve.update && this.curve.update();
      this.bbox = this.curve.bbox();
    }
    this.width = w;
  },

  updateWithPoints(_p1, _p2) {
    const p1 = _p1 || this.getGlobalP1();
    const p2 = _p2 || this.getGlobalP2();
    const measures = this.getLineMeasuresBetweenPoints(p1, p2);
    const w = measures.distance / this.scaleX;
    this.updatePointsCoords(w);
    this.set({
      left: toFixed(measures.center.x, variables.numberPrecision),
      top: toFixed(measures.center.y, variables.numberPrecision),
      angle: measures.degrees,
    });
  },

  updateWithP1Point(p1) {
    this.updateWithPoints(p1, this.getGlobalP2());
  },

  updateWithP2Point(p2) {
    this.updateWithPoints(this.getGlobalP1(), p2);
  },

  updateWithPoint(index, p) {
    if (index === 1) {
      this.updateWithP1Point(p);
    } else if (index === 2) {
      this.updateWithP2Point(p);
    }
  },

  addCanvasListeners() {
    if (!this.canvas) {
      return;
    }
    // Be sure to remove all existing listeners before adding new ones
    this.removeCanvasListeners();
    this.canvas.on('selection:cleared', this.canvasSelectionClearedHandler);
    this.canvas.on('mouse:down', this.canvasMouseDownHandler);
    this.canvas.on('mouse:up', this.canvasMouseUpHandler);
    this.canvas.on('mouse:move', this.canvasMouseMoveHandler);
  },

  removeCanvasListeners() {
    if (!this.canvas) {
      return;
    }
    this.canvas.off('selection:cleared', this.canvasSelectionClearedHandler);
    this.canvas.off('mouse:down', this.canvasMouseDownHandler);
    this.canvas.off('mouse:up', this.canvasMouseUpHandler);
    this.canvas.off('mouse:move', this.canvasMouseMoveHandler);
  },

  // Ludus ObjectSelection implementation
  // Return true or false to tell whether the keystroke was handled or not
  onKeyDownWithKey(key) {
    if (key.is(keyboard.SHIFT)) {
      this.shiftKeyActive = true;
      return true;
    }
    return false;
  },

  // Ludus ObjectSelection implementation
  // Return true or false to tell whether the keystroke was handled or not
  onKeyUpWithKey(key) {
    if (key.is(keyboard.SHIFT)) {
      this.shiftKeyActive = false;
      return true;
    }
    return false;
  },

  // Object subclass implementation
  onSelect() {
    this.setSelected(true);
    return false; // return false to allow selection (yes, this is counterintuitive)
  },

  // Object subclass implementation
  onDeselect() {
    this.setSelected(false);
    return false; // return false to allow deselection (yes, this is counterintuitive)
  },

  setSelected(value) {
    if (this.selected === value) {
      return;
    }
    this.selected = value;
    this.dirty = true;
  },

  getActiveHandleGlobalPoint() {
    if (this.activeHandleIndex === 1) {
      return this.getGlobalP1();
    }
    if (this.activeHandleIndex === 2) {
      return this.getGlobalP2();
    }
    return null;
  },

  onCanvasSelectionCleared() {
    if (this.canvas) {
      // when unselecting an active selection we must force a render
      this.updateCoords();
    }
  },

  onCanvasMouseDown(o) {
    if (this.canvas.skipTargetFind || this.activeHandleIndex) {
      return;
    }

    const pointer = this.canvas.getPointer(o.e, false);
    const coords = new fabric.Point(pointer.x, pointer.y);
    const isInsideControlP = this.isPointInsideControlPoint(coords);
    const isInsideP1Handle =
      !isInsideControlP && this.isPointInsideP1Handle(coords);
    const isInsideP2Handle =
      !isInsideControlP &&
      !isInsideP1Handle &&
      this.isPointInsideP2Handle(coords);

    if (isInsideControlP) {
      this.canvas.setCursor('-webkit-grabbing');
    }

    if ((isInsideP1Handle || isInsideP2Handle) && !isInsideControlP) {
      if (o.target === this) {
        if (this.canvas.selection) {
          this.canvasNeedsSelection = true;
          this.canvas.selection = false;
        }
        this.canvas.setCursor('-webkit-grabbing');
        this.set({
          lockMovementX: true,
          lockMovementY: true,
        });
        this.activeHandleIndex = isInsideP1Handle ? 1 : 2;
        this.innerTargetCoords = coords.subtract(
          this.getActiveHandleGlobalPoint(),
        );
        this.canvas.setActiveObject(this);
      }
    }
  },

  onCanvasMouseMove(o) {
    if (this.canvas.skipTargetFind) {
      return;
    }
    const pointer = this.canvas.getPointer(o.e, false);
    let coords = new fabric.Point(pointer.x, pointer.y);

    const isInsideCP = this.isPointInsideControlPoint(coords);
    const isInsideP1Handle = !isInsideCP && this.isPointInsideP1Handle(coords);
    const isInsideP2Handle =
      !isInsideCP && !isInsideP1Handle && this.isPointInsideP2Handle(coords);

    if (this.activeHandleIndex) {
      this._shouldCache = false;
      this.canvas.setCursor('-webkit-grabbing');

      coords = coords.subtract(this.innerTargetCoords);
      coords.x = Math.max(coords.x, this.canvas.vptCoords.tl.x);
      coords.x = Math.min(coords.x, this.canvas.vptCoords.br.x);
      coords.y = Math.max(coords.y, this.canvas.vptCoords.tl.y);
      coords.y = Math.min(coords.y, this.canvas.vptCoords.br.y);

      const minLength = 0;
      let newP1;
      let newP2;
      let newPoint;

      if (this.activeHandleIndex === 1) {
        newP1 = coords;
        const p2 = this.getGlobalP2();
        // Make sure line is not too short
        const newLength = newP1.distanceFrom(p2);
        if (newLength < minLength) {
          newP1 = extrapolatePoints(p2, newP1, minLength / newLength);
        }
        // Snap rotation if shift key is active
        if (this.shiftKeyActive) {
          const measures = this.getLineMeasuresBetweenPoints(newP1, p2);
          const rad = fabric.util.degreesToRadians(
            getSnapAngleForAngle(measures.degrees),
          );
          const x = p2.x - Math.cos(rad) * measures.distance;
          const y = p2.y - Math.sin(rad) * measures.distance;
          newP1 = new fabric.Point(x, y);
        }
        this.updateWithPoints(newP1, p2);

        newPoint = newP1;
      } else {
        const p1 = this.getGlobalP1();
        newP2 = coords;
        // Make sure line is not too short
        const newLength = p1.distanceFrom(newP2);
        if (newLength < minLength) {
          newP2 = extrapolatePoints(p1, newP2, minLength / newLength);
        }
        // Snap rotation if shift key is active
        if (this.shiftKeyActive) {
          const measures = this.getLineMeasuresBetweenPoints(p1, newP2);
          const rad = fabric.util.degreesToRadians(
            getSnapAngleForAngle(measures.degrees),
          );
          const x = p1.x + Math.cos(rad) * measures.distance;
          const y = p1.y + Math.sin(rad) * measures.distance;
          newP2 = new fabric.Point(x, y);
        }
        this.updateWithPoints(p1, newP2);
        newPoint = newP2;
      }

      this.setCoords();
      // this.updatePointsCoords(this.width);
      this.isMoving = true;
      this.dirty = true;
      if (this.canvas) {
        this.canvas.requestRenderAll();
      }
      this.dispatchHandleMoving(
        o.e,
        this.activeHandleIndex,
        newPoint,
        !this.shiftKeyActive,
      );
      this.dispatchRotating(o.e);
    } else if (isInsideP1Handle || isInsideP2Handle || isInsideCP) {
      if (o.target === this) {
        this._shouldCache = false;
        this.canvas.setCursor('-webkit-grab');
      }
    } else if (this.group && o.target === this.group) {
      this._shouldCache = false;
    } else {
      this._shouldCache = true;
    }
  },

  onCanvasMouseUp(o) {
    if (o.target !== this && !(this.group && o.target === this.group)) {
      return;
    }

    if (this.canvas.skipTargetFind) {
      return;
    }
    this._shouldCache = true;
    if (!this.activeHandleIndex) {
      return;
    }

    if (this.canvasNeedsSelection) {
      this.canvasNeedsSelection = false;
      this.canvas.selection = true;
    }
    this.set({
      lockMovementX: false,
      lockMovementY: false,
    });

    if (this.activeHandleIndex) {
      this.canvas.setCursor('-webkit-grab');
    }

    this.activeHandleIndex = 0;
    this.isMoving = false;
    this.dirty = true;
    this.dispatchModified(o.e);
  },

  rotate(angle) {
    this.callSuper('rotate', angle);
    this.setCoords();
  },

  _set(key, value) {
    if (!this.initialized) {
      this.callSuper('_set', key, value);
      return this;
    }
    const newValue = value;
    const previousArrow1 = this.arrow1;
    const previousArrow2 = this.arrow2;

    if (key === 'thickness') {
      this.strokeWidth = Math.min(this.strokeWidth, newValue * 4);
      this.canvas.requestRenderAll();
    }

    if (key === 'fill') {
      this.stroke = newValue;
      this.canvas.requestRenderAll();
    }

    if (key === 'strokeWidth') {
      this.callSuper('_set', key, newValue);
      this.applyStrokeStyle();
    }

    if (key === 'width') {
      this.updatePointsCoords(newValue);
      return this;
    }

    this.callSuper('_set', key, newValue);

    if (pointProperties.includes(key)) {
      const points = [this.x1, this.y1, this.x2, this.y2];
      const { p1, p2 } = this.getPointsFromCoordsArray(points);
      let newP1 = p1;
      let newP2 = p2;
      // Make sure line is not too short
      const minLength = this.getMinLengthFromArrowSize(this.thickness);
      const newLength = p1.distanceFrom(p2);
      if (newLength < minLength) {
        const pointChanged = parseInt(key.substr(1, 1), 10);
        if (pointChanged === 1) {
          newP1 = extrapolatePoints(p2, p1, minLength / newLength);
          newP2 = p2;
        } else {
          newP1 = p1;
          newP2 = extrapolatePoints(p1, p2, minLength / newLength);
        }
      }
      this.updateWithPoints(newP1, newP2);
    }

    if (key === 'length') {
      // Flip line if length is negative
      let points = [this.x1, this.y1, this.x2, this.y2];
      if (newValue < 0) {
        points = [this.x2, this.y2, this.x1, this.y1];
      }
      const { p1, p2 } = this.getPointsFromCoordsArray(points);
      const newLength = Math.abs(newValue);
      const minLength = this.getMinLengthFromArrowSize(this.strokeWidth);
      const currentLength = p1.distanceFrom(p2);
      // Make sure line is not too short
      const newP2 = extrapolatePoints(
        p1,
        p2,
        Math.max(newLength, minLength) / currentLength,
      );
      this.updateWithPoints(p1, newP2);
    }

    if (key === 'arrow1' && !previousArrow1 && newValue === true) {
      const points = [this.x1, this.y1, this.x2, this.y2];
      const { p1, p2 } = this.getPointsFromCoordsArray(points);
      const minLength = this.getMinLengthFromArrowSize(this.strokeWidth);
      const currentLength = p1.distanceFrom(p2);

      // Make sure line is not too short
      if (currentLength < minLength) {
        const newP1 = extrapolatePoints(p2, p1, minLength / currentLength);
        this.updateWithPoints(newP1, p2);
      }
    }

    if (key === 'arrow2' && !previousArrow2 && newValue === true) {
      const points = [this.x1, this.y1, this.x2, this.y2];
      const { p1, p2 } = this.getPointsFromCoordsArray(points);
      const minLength = this.getMinLengthFromArrowSize(this.strokeWidth);
      const currentLength = p1.distanceFrom(p2);

      // Make sure line is not too short
      if (currentLength < minLength) {
        const newP2 = extrapolatePoints(p1, p2, minLength / currentLength);
        this.updateWithPoints(p1, newP2);
      }
    }

    if (key === 'canvas') {
      if (newValue) {
        // Canvas is set, we can use it

        this.addCanvasListeners();
      } else {
        this.removeCanvasListeners();
      }
    }

    return this;
  },

  dispose: function() {
    this.removeCanvasListeners();
  },

  applyScaleY: function(value) {
    const cp = new fabric.Point(this.cp.x, this.cp.y);
    const t = [1, 0, 0, value, 0, 0];
    this.setCp(fabric.util.transformPoint(cp, t));
  },

  _getCacheCanvasDimensions: function() {
    var retina = this.canvas.getRetinaScaling();
    var zoom = this.canvas.getZoom() * retina;
    const bbox = this.bbox || this.curve.bbox();
    const padding = (this.strokeWidth + this.arrowSize) * zoom;

    return {
      width: bbox.x.size * zoom + padding,
      height: bbox.y.size * zoom + padding,
      zoom: zoom,
      zoomY: 1,
      x: -bbox.x.min * zoom + padding / 2,
      y: -bbox.y.min * zoom + padding / 2,
    };
  },

  _updateCacheCanvas() {
    var canvas = this._cacheCanvas;
    const dims = this._getCacheCanvasDimensions();
    var width = dims.width;
    var height = dims.height;
    const dimensionsChanged =
      width !== this.cacheWidth || height !== this.cacheHeight;
    this.canCache = width < 2500 && height < 2500;

    if (dimensionsChanged && this.canCache) {
      canvas.width = Math.ceil(width);
      canvas.height = Math.ceil(height);
      this.cacheWidth = width;
      this.cacheHeight = height;
      this.cacheTranslationX = dims.x;
      this.cacheTranslationY = dims.y;

      this.zoomX = dims.zoom;
      this.zoomY = dims.zoom;
      this._cacheContext.translate(
        this.cacheTranslationX,
        this.cacheTranslationY,
      );
      this._cacheContext.scale(dims.zoom, dims.zoom);

      return true;
    }

    return false;
  },

  getRenderedCurve(pt1, pt2) {
    const curve = this.curve;
    const canSkip =
      this.width === this._previousState.width &&
      this.cp.x === this._previousState.cp.x &&
      this.cp.y === this._previousState.cp.y &&
      this.arrowSize === this._previousState.arrowSize &&
      this.arrow1 === this._previousState.arrow1 &&
      this.arrow2 === this._previousState.arrow2;
    if (!canSkip) {
      this._previousState = {};
      this.intersectStart = null;
      this.intersectEnd = null;
    }
    let intersectEnd =
      canSkip && this._previousState.intersectEnd !== null
        ? this._previousState.intersectEnd
        : null;
    let intersectStart =
      canSkip && this._previousState.intersectStart !== null
        ? this._previousState.intersectStart
        : null;
    const arrowSize = this.arrowSize;
    const r = arrowSize;

    if (this.arrow1 && !intersectStart) {
      for (let index = 0; index < 4; index++) {
        // TODO ONLY RECOMPUTE IF one of the three points changed
        const arc = arcsFromCircle(pt1.x, r, this.circle1, index);
        if (this.circle1) {
          this.circle1[index] = arc;
        } else {
          this.circle1 = [arc];
        }
        if (intersectStart) {
          break;
        } else {
          intersectStart = curve.intersects(arc).pop();
          this.intersectStart = intersectStart;
        }
      }
    }

    if (this.arrow2 && !intersectEnd) {
      for (let index = 0; index < 4; index++) {
        const arc = arcsFromCircle(pt2.x, r, this.circle2, index);
        if (this.circle2) {
          this.circle2[index] = arc;
        } else {
          this.circle2 = [arc];
        }
        if (intersectEnd) {
          break;
        } else {
          intersectEnd = curve.intersects(arc).pop();
          this.intersectEnd = intersectEnd;
        }
      }
    }

    const arrowRatio = arrowSize / this.width;
    const minArrowOffset1 = this.arrow1 ? Math.min(arrowRatio, 1) : 0; // use min 1 for cases where arrow is smaller than line
    const minArrowOffset2 = this.arrow2 ? Math.max(1 - arrowRatio, 0) : 1; // use max 0 for cases where arrow is smaller than line
    const ratioStart = intersectStart
      ? intersectStart.split('/')[0]
      : minArrowOffset1;
    const ratioEnd = intersectEnd
      ? intersectEnd.split('/')[0]
      : minArrowOffset2;

    return {
      splitedCurve: curve.split(ratioStart, ratioEnd),
      ratioEnd,
      ratioStart,
    };
  },

  getPointAngle(ratio, pt) {
    var point = this.curve.get(ratio);
    var dx = pt.x - point.x;
    var dy = pt.y - point.y;
    return Math.atan2(dy, dx);
  },

  _updateCache() {
    if (this._shouldCache) {
      const bbox = this.bbox;
      const padding = this.strokeWidth + this.arrowSize;
      if (!this.offscreen) {
        this.offscreen = OffscreenCanvas
          ? new OffscreenCanvas(bbox.x.size + padding, bbox.x.size + padding)
          : window.document.createElement('canvas');
        this.ctxCache = this.offscreen.getContext('2d');
        this.ctxCache.imageSmoothingEnabled = false;
      }
      if (this.offscreen.width !== bbox.x.size + padding) {
        this.offscreen.width = bbox.x.size + padding;
      }
      if (this.offscreen.height !== bbox.y.size + padding) {
        this.offscreen.height = bbox.y.size + padding;
      }

      const ctx = this.ctxCache;
      const offsetX = -bbox.x.min + padding / 2;
      const offsetY = -bbox.y.min + padding / 2;
      this.offsetX = offsetX;
      this.offsetY = offsetY;

      ctx.setTransform(1, 0, 0, 1, offsetX, offsetY);
      this._renderLine(ctx);
    }
  },

  shouldCache() {
    if (this.canvas.interactive) {
      return this._shouldCache && this.canCache;
    } else {
      return false;
    }
  },

  _renderLine(ctx) {
    const p1 = this.getLocalP1();
    const p2 = this.getLocalP2();

    const arrowSize = getTriangleSizeByThickness(this.arrowSize);
    ctx.lineWidth = this.strokeWidth;
    ctx.strokeStyle = this.stroke;
    ctx.lineCap = 'round';

    if (this.strokeDashArray) {
      ctx.setLineDash(this.strokeDashArray);
    }

    ctx.beginPath();
    const pt1 = { x: p1.x, y: p1.y };
    const pt2 = { x: p2.x, y: p2.y };
    const { splitedCurve, ratioEnd, ratioStart } = this.getRenderedCurve(
      pt1,
      pt2,
      arrowSize,
    );

    const points = splitedCurve.points || splitedCurve.left.points;
    ctx.moveTo(points[0].x, points[0].y);
    drawCurve(ctx, splitedCurve);
    ctx.stroke();
    // ctx.fill();
    ctx.closePath();

    // DRAW START ARROW
    if (this.arrow1) {
      ctx.save();

      var startAngle = this.getPointAngle(ratioStart, pt1) || Math.PI; // At 0 angle, arrow must point outward at 180° (Math.PI radians)
      ctx.translate(pt1.x, pt1.y);
      ctx.rotate(startAngle);
      ctx.beginPath();
      drawLine(ctx, { x: 0, y: 0 }, { x: -arrowSize, y: arrowSize / 2 });
      drawLine(ctx, { x: -arrowSize, y: -arrowSize / 2 }, { x: 0, y: 0 });
      drawLine(
        ctx,
        { x: -arrowSize, y: arrowSize / 2 },
        { x: -arrowSize, y: -arrowSize / 2 },
      );
      ctx.closePath();
      ctx.fillStyle = this.stroke;
      ctx.fill();
      ctx.restore();
    }
    if (this.arrow2) {
      // DRAW END ARROW
      ctx.save();

      var endingAngle = this.getPointAngle(ratioEnd, pt2);
      ctx.translate(pt2.x, pt2.y);
      ctx.rotate(endingAngle);
      ctx.beginPath();
      drawLine(ctx, { x: 0, y: 0 }, { x: -arrowSize, y: arrowSize / 2 });
      drawLine(ctx, { x: -arrowSize, y: -arrowSize / 2 }, { x: 0, y: 0 });
      drawLine(
        ctx,
        { x: -arrowSize, y: arrowSize / 2 },
        { x: -arrowSize, y: -arrowSize / 2 },
      );
      ctx.closePath();
      ctx.fillStyle = this.stroke;
      ctx.fill();
      ctx.restore();
    }
  },

  render: function(ctx) {
    // do not render if width/height are zeros or object is not visible
    if (this.isNotVisible()) {
      return;
    }
    if (
      this.canvas &&
      this.canvas.skipOffscreen &&
      !this.group &&
      !this.isOnScreen()
    ) {
      return;
    }
    ctx.save();
    this._setupCompositeOperation(ctx);
    this.drawSelectionBackground(ctx);
    this.transform(ctx);
    this._setOpacity(ctx);
    this._setShadow(ctx, this);
    if (this.transformMatrix) {
      ctx.transform.apply(ctx, this.transformMatrix);
    }
    this.clipTo && fabric.util.clipContext(this, ctx);
    const { width, height } = this._getCacheCanvasDimensions();
    this.canCache = width < 2500 && height < 2500;

    if (this.shouldCache()) {
      this.renderCache();
      this.drawCacheOnCanvas(ctx);
    } else {
      this._removeCacheCanvas();
      this.dirty = false;
      this.drawObject(ctx);
    }
    this.clipTo && ctx.restore();
    ctx.restore();
  },

  _render(ctx) {
    if (!this.curve) {
      return;
    }
    setTimeout(() => {
      this._previousState = {
        arrowSize: this.arrowSize,
        cp: this.cp,
        width: this.width,
        intersectEnd: this.intersectEnd,
        intersectStart: this.intersectStart,
        arrow1: this.arrow1,
        arrow2: this.arrow2,
      };
    }, 0);
    this._renderLine(ctx);
  },

  _getNonTransformedDimensions() {
    const w = this.width;
    const h = this.height;
    return { x: w, y: h };
  },

  // Overriding fabric.Object.prototype._findTargetCorner so we can trick Fabric controls engine
  // to think ml & mr controls are usable, allowing grabbing handles when the arrow is selected
  // below another object
  _findTargetCorner(pointer) {
    if (this.selected) {
      const coords = this.canvas.restorePointerVpt(
        new fabric.Point(pointer.x, pointer.y),
      );

      const isInsideP1Handle = this.isPointInsideP1Handle(coords);
      const isInsideP2Handle = this.isPointInsideP2Handle(coords);
      const isInsideTensionHandle = this.isPointInsideControlPoint(coords);
      if (isInsideTensionHandle) {
        return { name: 'custom', prop: 'lineTension' };
      }
      if (isInsideP1Handle) {
        return 'p1';
      }
      if (isInsideP2Handle) {
        return 'p2';
      }
    }
    return false;
  },

  // Overriding fabric.Object.prototype.drawControls so we can draw our handles
  // regardless of isControlVisible(), because if we set these visible ('ml' and 'mr'),
  // then fabric allows modifying the line in ways we don't want (distort)
  // Original code start
  drawBorders(ctx, styleOverride) {
    styleOverride = styleOverride || {};
    const wh = this._calculateCurrentDimensions();
    const strokeWidth = 1 / this.borderScaleFactor;
    const width = wh.x + strokeWidth;
    const height = wh.y + strokeWidth;
    const scaleOffset = styleOverride.cornerSize || this.cornerSize;

    const left = -(width + scaleOffset) / 2;
    const top = -(height + scaleOffset) / 2;

    ctx.save();
    const prevStrokeStyle = ctx.strokeStyle;
    ctx.strokeStyle = styleOverride.borderColor || this.borderColor;
    this.drawHandleLine(
      ctx,
      left,
      top + height / 2,
      left + width,
      top + height / 2,
    );

    ctx.strokeStyle = prevStrokeStyle;
    ctx.restore();
    return this;
  },

  drawBordersInGroup(ctx, options, styleOverride) {
    styleOverride = styleOverride || {};

    const strokeWidth = 1 / this.borderScaleFactor;
    const width = this.width * options.scaleX + strokeWidth;
    const height = this.height * options.scaleY + strokeWidth;
    const scaleOffset = styleOverride.cornerSize || this.cornerSize;
    const left = -(width + scaleOffset) / 2;
    const top = -(height + scaleOffset) / 2;

    ctx.save();
    const prevStrokeStyle = ctx.strokeStyle;
    ctx.strokeStyle = styleOverride.borderColor || this.borderColor;
    this.drawHandleLine(
      ctx,
      left,
      top + height / 2,
      left + width,
      top + height / 2,
      options,
    );

    ctx.strokeStyle = prevStrokeStyle;
    ctx.restore();
    return this;
  },

  // Original code start
  drawControls(ctx, styleOverride) {
    // Custom code start
    // Only draw handles if the line is either selected or a handle is being dragged around
    if (!this.selected && !this.activeHandleIndex) {
      return this;
    }
    // Custom code end

    // Original code start
    styleOverride = styleOverride || {};

    const wh = this._calculateCurrentDimensions();
    const width = wh.x;
    const height = wh.y;
    const scaleOffset = styleOverride.cornerSize || this.cornerSize;
    const left = -(width + scaleOffset) / 2;
    const top = -(height + scaleOffset) / 2;
    const transparentCorners =
      typeof styleOverride.transparentCorners !== 'undefined'
        ? styleOverride.transparentCorners
        : this.transparentCorners;
    const methodName = transparentCorners ? 'stroke' : 'fill';

    ctx.save();
    ctx.strokeStyle = ctx.fillStyle =
      styleOverride.cornerColor || this.cornerColor;
    if (!this.transparentCorners) {
      ctx.strokeStyle =
        styleOverride.cornerStrokeColor || this.cornerStrokeColor;
    }

    // Original code end

    // Custom code start
    // 'left' and 'right' values are in fact our p1 and p2, let's just use these here,
    // as they're the official way and work well.
    const prevStrokeStyle = ctx.strokeStyle;
    ctx.strokeStyle = styleOverride.borderColor || this.borderColor;
    ctx.strokeStyle = prevStrokeStyle;

    const pt1 = new fabric.Point(left, top + height / 2);
    const pt2 = new fabric.Point(left + width, top + height / 2);

    this.drawHandle(methodName, ctx, pt1.x, pt1.y);
    this.drawHandle(methodName, ctx, pt2.x, pt2.y);

    // draw control point handle
    const zoom = this.canvas.getZoom();
    const size = this.cornerSize / 2;
    const pointOnLine = getPointOnLine(this.cp.x, 0, this.cp.y, 0, 0.5);
    const curvePoint = new fabric.Point(
      pointOnLine.x * zoom,
      pointOnLine.y * zoom,
    );
    this.cpVisible =
      pt1.distanceFrom(curvePoint) > size * 2 &&
      pt2.distanceFrom(curvePoint) > size * 2;

    if (this.cpVisible) {
      ctx.closePath();
      ctx.beginPath();
      ctx.arc(
        curvePoint.x * this.scaleX,
        curvePoint.y * this.scaleY,
        size,
        0,
        2 * Math.PI,
      );
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
    return this;
  },

  // Mostly copied from fabric.Object.prototype._drawControl
  drawHandle(methodName, ctx, left, top) {
    const size = this.cornerSize;
    const halfSize = size / 2;

    ctx.beginPath();
    ctx.arc(left + halfSize, top + halfSize, halfSize, 0, RAD_360, false);
    if (methodName === 'fill') {
      ctx.fill();
    }
    ctx.stroke();
    ctx.closePath();
  },

  drawHandleLine(ctx, l1, t1, l2, t2, options) {
    const size = this.cornerSize;
    const halfSize = size / 2;
    const x2 = l2;
    const y2 = t2;
    const zoom = this.canvas.getZoom();
    ctx.beginPath();
    ctx.moveTo(l1 + halfSize, t1 + halfSize);
    ctx.quadraticCurveTo(
      this.cp.x * (options?.scaleX || this.scaleX * zoom),
      this.cp.y * (options?.scaleY || this.scaleY * zoom),
      x2 + halfSize,
      y2 + halfSize,
    );

    ctx.stroke();
    ctx.closePath();
  },

  updateGradient() {
    if (!this.fill || !this.fill.coords) {
      return;
    }
    const angle = getGradientAngleFromFabricGradientCoords(this.fill.coords);
    const gradient = getFabricGradient(
      {
        ...this.fill,
        angle,
      },
      this.strokeWidth,
      this.strokeWidth,
      this.width / 2,
    );
    this.fill = gradient;
  },

  getLineMeasuresBetweenPoints(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const radians = (Math.atan2(dy, dx) + RAD_360) % RAD_360; // output 0 - 2PI
    const degrees = fabric.util.radiansToDegrees(radians);
    const center = p1.midPointFrom(p2);
    const distance = p1.distanceFrom(p2);
    return {
      p1: p1.clone(),
      p2: p2.clone(),
      center,
      radians,
      degrees,
      distance,
    };
  },

  dispatchHandleMoving(e, handleIndex, point, snap) {
    const payload = {
      e,
      handleIndex,
      point,
      snap,
    };
    this.canvas.fire('object:handle:moving', { target: this, ...payload });
    this.fire('handle:moving', payload);
  },

  dispatchRotating(e) {
    this.canvas.fire('object:rotating', { target: this, e });
    this.fire('rotating', { e });
  },

  dispatchModified(e) {
    this.canvas.fire('object:modified', { target: this, e });
    this.fire('modified', { e });
  },

  toObject(propertiesToInclude) {
    const _propertiesToInclude = propertiesToInclude
      ? pull(propertiesToInclude, 'perPixelTargetFind', 'lockUniScaling')
      : [];
    return this.callSuper(
      'toObject',
      _propertiesToInclude.concat(ownProperties),
    );
  },

  _toSVG() {
    const transformMatrix = [...this.ludus.transformMatrix];
    const options = fabric.util.qrDecompose(transformMatrix);
    this.setPositionByOrigin(
      { x: options.translateX, y: options.translateY },
      'center',
      'center',
    );
    this.scaleX = options.scaleX;
    this.scaleY = options.scaleY;
    this.angle = options.angle;
    this.skewX = options.skewX;
    this.skewY = options.skewY;

    const p1 = this.getLocalP1();
    const p2 = this.getLocalP2();
    const pt1 = { x: p1.x, y: p1.y };
    const pt2 = { x: p2.x, y: p2.y };
    const { splitedCurve, ratioEnd, ratioStart } = this.getRenderedCurve(
      pt1,
      pt2,
    );
    const arrowSize = this.arrowSize;

    var startAngle = ratioStart
      ? this.getPointAngle(ratioStart, pt1) || Math.PI
      : 0; // At 0 angle, arrow must point outward at 180° (Math.PI radians)
    var endAngle = this.getPointAngle(ratioEnd, pt2);

    return [
      '<g opacity=',
      this.opacity,
      '>',
      '<path ',
      'stroke="',
      this.stroke,
      '"stroke-width="',
      this.strokeWidth,
      '"stroke-dasharray="',
      this.strokeDashArray,
      '"stroke-linecap="',
      'round',
      '"d="',
      splitedCurve.toSVG ? splitedCurve.toSVG() : splitedCurve.left.toSVG(),
      '" />\n',
      this.arrow1
        ? `<g transform="translate(${p1.x}, ${p1.y}) 
           rotate(${(startAngle / Math.PI) * 180 -
             180})"><path d="M 0 0 l${arrowSize} -${arrowSize /
            2} l0 ${arrowSize} l-${arrowSize} -${arrowSize / 2}" fill="${
            this.stroke
          }"  /></g>`
        : null,
      this.arrow2
        ? `<g transform="translate(${p2.x}, ${p2.y}) rotate(${(endAngle /
            Math.PI) *
            180})">
          <path d="M 0 0 l-${arrowSize} -${arrowSize /
            2} l0 ${arrowSize} l${arrowSize} -${arrowSize / 2}" fill="${
            this.stroke
          }"  /></g>`
        : null,
      '</g>\n',
    ];
  },
});

LudusLine.fromObject = function fromObject(object, callback) {
  const options = fabric.util.object.clone(object, true);
  if (callback) callback(new LudusLine(options));
};

LudusLine.fromElement = function(element, callback, options) {
  const extend = fabric.util.object.extend;
  options = options || {};
  var parsedAttributes = fabric.parseAttributes(
    element,
    fabric.Line.ATTRIBUTE_NAMES,
  );
  var points = [
    parsedAttributes.x1 || 0,
    parsedAttributes.y1 || 0,
    parsedAttributes.x2 || 0,
    parsedAttributes.y2 || 0,
  ];

  callback(
    new fabric.LudusLine({ ...points, ...extend(parsedAttributes, options) }),
  );
};

LudusLine.MIN_THICKNESS = MIN_THICKNESS;
LudusLine.MAX_THICKNESS = MAX_THICKNESS;

export default LudusLine;
